import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} CustomizerOption
 * @property {string} id
 * @property {string} label
 * @property {number} [price] - Price delta in minor units of the buyer's currency.
 * @property {number} [priceShop] - The same delta, unconverted, in the shop's own currency.
 * @property {boolean} [available]
 *
 * @typedef {object} CustomizerGroup
 * @property {string} id
 * @property {string} label
 * @property {CustomizerOption[]} options
 *
 * @typedef {object} CustomizerRule
 * @property {Record<string, string | string[]>} when
 * @property {boolean} [unavailable]
 * @property {number} [price]
 * @property {number} [priceShop]
 * @property {string} [message]
 *
 * @typedef {object} CustomizerVariant
 * @property {number} id
 * @property {number} price - Already converted to the buyer's currency by Shopify.
 * @property {number} compareAtPrice
 * @property {string} sku - Carries the configuration key, `CFG-<shop-currency cents>`.
 * @property {boolean} available
 *
 * @typedef {object} CustomizerConfig
 * @property {CustomizerGroup[]} groups
 * @property {CustomizerRule[]} rules
 * @property {string[]} [priceGroups] - Group ids that determine the price, if declared.
 * @property {number} basePrice
 * @property {number} basePriceShop
 * @property {number} compareAtDelta
 * @property {string} moneyFormat
 * @property {string} currency
 * @property {'display' | 'variant_match'} pricingMode
 * @property {CustomizerVariant[]} variants
 * @property {boolean} syncThemePrice
 * @property {string} formId
 * @property {Record<string, string>} strings
 * @property {{id: number, alt: string, src: string}[]} [media] - The product's own media.
 * @property {(CustomizerRule & {image?: string, alt?: string})[]} [comboImages] - Combination to media rules.
 * @property {string} [imagePattern] - e.g. `{shape}-{setting_color}`, matched against alt or filename.
 * @property {string[]} [photoAliases] - `option=borrowed`, for choices this product has not shot yet.
 */

/**
 * @typedef {object} ProductCustomizerRefs
 * @property {HTMLElement} [priceAmount]
 * @property {HTMLElement} [compareAmount]
 * @property {HTMLElement} [notice]
 * @property {HTMLElement} [summary]
 * @property {HTMLInputElement} [configProperty]
 * @property {HTMLInputElement} [priceProperty]
 */

/**
 * Interactive product configurator.
 *
 * Renders any number of independent option groups (gemstone shape, gemstone material, ring setting
 * material, ring setting colour), reprices the chosen combination, writes the selection into the
 * product form as line item properties, and — in `variant_match` pricing mode — repoints the form
 * at the variant whose price equals the configured total.
 *
 * @extends {Component<ProductCustomizerRefs>}
 */
class ProductCustomizer extends Component {
  /** @type {CustomizerConfig | null} */
  #config = null;

  /** Current selection, keyed by group id. @type {Record<string, string>} */
  #selection = {};

  /** @type {Element | null} */
  #section = null;

  /** Media id currently shown in the gallery; null until the first resolve. @type {number | null} */
  #currentMediaId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');
    this.#config = this.#readConfig();
    if (!this.#config) return;

    this.#selection = this.#readSelectionFromDom();
    this.addEventListener('change', this.#handleChange);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * The Section Rendering API may morph this block (for example when a native variant change moves
   * the base price). Re-read the freshly rendered config, restore the buyer's selection, recalculate.
   */
  updatedCallback() {
    super.updatedCallback();
    if (!this.#config) return;

    const config = this.#readConfig();
    if (config) this.#config = config;

    this.#restoreSelectionToDom();
    this.update();
  }

  /** @returns {CustomizerConfig | null} */
  #readConfig() {
    const script = this.querySelector('script[ref="configData"]');
    if (!script?.textContent) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      if (!Array.isArray(parsed?.groups) || parsed.groups.length === 0) return null;

      parsed.rules = (Array.isArray(parsed.rules) ? parsed.rules : []).map(withWhenClause);
      parsed.variants = Array.isArray(parsed.variants) ? parsed.variants : [];
      parsed.media = Array.isArray(parsed.media) ? parsed.media : [];
      parsed.comboImages = (Array.isArray(parsed.comboImages) ? parsed.comboImages : []).map(withWhenClause);

      // Rows the product prices through its own options, rather than through a sku convention.
      parsed.nativeRows = nativeRowsOf(parsed);

      return parsed;
    } catch (error) {
      console.warn('[product-customizer] Invalid configuration JSON:', error);
      return null;
    }
  }

  /**
   * Seeds the selection from whatever the server rendered as checked, falling back to the first
   * selectable option so we always hold a complete combination.
   *
   * @returns {Record<string, string>}
   */
  #readSelectionFromDom() {
    /** @type {Record<string, string>} */
    const selection = {};

    for (const group of this.#config?.groups ?? []) {
      const checked = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`[data-group-id="${CSS.escape(group.id)}"] input:checked`)
      );
      const fallback = group.options.find((option) => option.available !== false) ?? group.options[0];
      selection[group.id] = checked?.value ?? fallback?.id ?? '';
    }

    return selection;
  }

  /** Re-applies the held selection to the controls after a morph replaced them. */
  #restoreSelectionToDom() {
    for (const [groupId, optionId] of Object.entries(this.#selection)) {
      const control = this.#findControl(groupId, optionId);

      if (control instanceof HTMLOptionElement) {
        control.selected = true;
      } else if (control instanceof HTMLInputElement) {
        control.checked = true;
      }
    }
  }

  /**
   * Resolves the control backing an option — a radio for button/tile/swatch groups, an `<option>`
   * for dropdown groups.
   *
   * @param {string} groupId
   * @param {string} optionId
   * @returns {HTMLInputElement | HTMLOptionElement | null}
   */
  #findControl(groupId, optionId) {
    const group = this.querySelector(`[data-group-id="${CSS.escape(groupId)}"]`);
    if (!group) return null;

    const value = CSS.escape(optionId);
    return /** @type {HTMLInputElement | HTMLOptionElement | null} */ (
      group.querySelector(`input[value="${value}"], option[value="${value}"]`)
    );
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLSelectElement)) return;

    const groupId = input.closest('[data-group-id]')?.getAttribute('data-group-id');
    if (!groupId) return;

    this.#selection[groupId] = input.value;
    this.update();
  };

  /**
   * A native variant change re-renders the section, so recalculate once the new markup lands —
   * otherwise our price override is left showing a stale base.
   *
   * @param {{promise?: Promise<unknown>}} event
   */
  #handleProductSelect = (event) => {
    event.promise
      ?.then(() => {
        const config = this.#readConfig();
        if (config) this.#config = config;

        this.#restoreSelectionToDom();
        this.update();
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[product-customizer] Event promise rejected:', error);
      });
  };

  /**
   * Reprices the current combination and pushes the result into the price display, the option
   * buttons, the line item properties and the form's variant id.
   */
  update() {
    if (!this.#config) return;

    // Settle first: pricing and photographs should describe a combination the buyer can actually
    // see, not whichever option happened to be rendered checked.
    this.#settleSelection();

    const result = this.#resolve(this.#selection);
    const match = this.#matchVariant(this.#selection, result);

    // Under variant_match the matched variant *is* the price: it is the amount the cart will
    // charge, already converted and rounded by Shopify for this market. Displaying our own sum
    // instead would reintroduce exactly the mismatch this mode exists to remove.
    //
    // A row at zero is one nobody has priced yet. Showing it as $0.00 would advertise a free ring,
    // so the configured total stands and #applyVariant blocks the sale.
    if (match && match.price > 0) {
      const config = /** @type {CustomizerConfig} */ (this.#config);
      result.total = match.price;
      result.compareAt =
        match.compareAtPrice > match.price ? match.compareAtPrice : result.total + config.compareAtDelta;
    }

    this.#renderOptionStates();
    this.#renderGroupLabels(result);
    this.#renderComboMedia();
    this.#renderPrice(result);
    this.#renderSummary(result);
    this.#writeProperties(result);
    this.#applyVariant(result, match);
  }

  /**
   * Finds the variant standing in for this combination.
   *
   * Keyed on the **shop-currency** total carried in the variant SKU (`CFG-<cents>`), not on the
   * displayed price. Shopify converts and rounds each variant price independently for the buyer's
   * market, so a total we summed from individually-converted surcharges almost never lands on the
   * same cent as the converted variant — matching on price works only in the shop's own currency.
   *
   * Falls back to price equality when no variant carries a key, which is the pre-existing behaviour
   * for single-currency stores that set their variants up by hand.
   *
   * @param {Record<string, string>} selection
   * @param {{total: number, totalShop: number}} [result] - omitted for a hypothetical lookup
   * @returns {CustomizerVariant | null}
   */
  #matchVariant(selection, result) {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    if (config.pricingMode !== 'variant_match') return null;

    // A product whose option rows are wired to its own product options already states which variant
    // a selection means -- Shopify keeps that mapping itself. It is authoritative, so there is no
    // falling back to the sku or the price from here: a miss means the combination has no variant,
    // which is exactly what the buyer should be told.
    if (config.nativeRows) return this.#matchByOptions(selection);

    const keyed = config.variants.filter((variant) => variantKey(variant) !== null);

    // The key this selection should produce. Naming variants after the priced groups makes them
    // legible in the admin (`sapphire-6k`), so prefer that; otherwise fall back to the
    // shop-currency total, which is the same number in either currency.
    const priceGroups = config.priceGroups ?? [];
    const target = priceGroups.length
      ? priceGroups.map((groupId) => selection[groupId]).join('-')
      : String(result?.totalShop);

    // Without a total there is nothing to match a keyless variant against — this is a hypothetical
    // lookup (what would picking 18k cost?), which only name-keyed catalogues can answer.
    if (!priceGroups.length && !result) return null;

    const candidates = keyed.length > 0 ? keyed : config.variants;
    const matches =
      keyed.length > 0
        ? (/** @type {CustomizerVariant} */ variant) => variantKey(variant) === target
        : (/** @type {CustomizerVariant} */ variant) => variant.price === result.total;

    // Prefer a sellable match, but fall back to an unavailable one so the buyer is told the
    // combination is sold out rather than that it cannot be priced.
    return candidates.find((variant) => matches(variant) && variant.available) ?? candidates.find(matches) ?? null;
  }

  /**
   * The variant standing for this selection, read off the product's own options.
   *
   * Every row that is wired to a product option has to agree. Rows that are not -- shape and
   * setting colour on a ring priced by stone and karat -- are free choices the buyer records
   * without moving the price, so they take no part in the lookup.
   *
   * @param {Record<string, string>} selection
   * @returns {CustomizerVariant | null}
   */
  #matchByOptions(selection) {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    const rows = config.nativeRows;
    if (!rows) return null;

    const wanted = [];
    for (const [groupId, byOption] of Object.entries(rows)) {
      const value = byOption[selection[groupId]];
      // The chip names an option the product's grid does not carry, so no variant can exist for it.
      if (!value) return null;
      wanted.push(value);
    }
    if (wanted.length === 0) return null;

    const matches = (/** @type {CustomizerVariant} */ variant) =>
      wanted.every((value) => (variant.options ?? []).includes(value));

    // Prefer a sellable match, but fall back to an unavailable one so the buyer is told the
    // combination is sold out rather than that it cannot be priced.
    return config.variants.find((variant) => matches(variant) && variant.available) ??
      config.variants.find(matches) ??
      null;
  }

  /**
   * Prices a combination and collects any rule that blocks it.
   *
   * @param {Record<string, string>} selection
   * @returns {{
   *   total: number,
   *   totalShop: number,
   *   compareAt: number,
   *   available: boolean,
   *   message: string,
   *   chosen: {group: CustomizerGroup, option: CustomizerOption}[],
   * }}
   */
  #resolve(selection) {
    const config = /** @type {CustomizerConfig} */ (this.#config);

    let total = config.basePrice;
    // The same sum left in the shop's own currency — the key variant_match looks the variant up by.
    let totalShop = config.basePriceShop ?? config.basePrice;
    let available = true;
    let message = '';
    /** @type {{group: CustomizerGroup, option: CustomizerOption}[]} */
    const chosen = [];

    for (const group of config.groups) {
      const option = group.options.find((candidate) => candidate.id === selection[group.id]);

      if (!option) {
        available = false;
        continue;
      }

      total += Number(option.price) || 0;
      totalShop += Number(option.priceShop ?? option.price) || 0;

      if (option.available === false) {
        available = false;
        message ||= config.strings.optionUnavailable;
      }

      chosen.push({ group, option });
    }

    for (const rule of config.rules) {
      if (!matchesRule(rule, selection)) continue;

      total += Number(rule.price) || 0;
      totalShop += Number(rule.priceShop ?? rule.price) || 0;

      if (rule.unavailable) {
        available = false;
        message ||= rule.message || config.strings.comboUnavailable;
      }
    }

    total = Math.max(0, Math.round(total));
    totalShop = Math.max(0, Math.round(totalShop));

    return { total, totalShop, compareAt: total + config.compareAtDelta, available, message, chosen };
  }

  /**
   * Is this product priced from its variant table rather than from surcharges in the catalogue?
   *
   * @returns {boolean}
   */
  #pricedFromVariants() {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    if (config.pricingMode !== 'variant_match') return false;
    if (config.nativeRows) return true;

    return (
      (config.priceGroups?.length ?? 0) > 0 &&
      config.variants.some((variant) => variantKey(variant) !== null)
    );
  }

  /**
   * What this option costs against the first option in its row, for the rest of the current
   * selection — the same thing the old fixed surcharges meant, but read off the price table so it
   * is exact for the combination in front of the buyer.
   *
   * @param {CustomizerGroup} group
   * @param {string} optionId
   * @returns {number | null} null when this row does not move the price, or the row is unpriced
   */
  #liveDelta(group, optionId) {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    if (!this.#pricedFromVariants()) return null;

    const priced = config.nativeRows ? Boolean(config.nativeRows[group.id]) : config.priceGroups?.includes(group.id);
    if (!priced) return null;

    const baseline = group.options.find((option) => option.available !== false) ?? group.options[0];
    if (!baseline) return null;

    const here = this.#matchVariant({ ...this.#selection, [group.id]: optionId });
    const from = this.#matchVariant({ ...this.#selection, [group.id]: baseline.id });
    if (!here || !from || here.price <= 0 || from.price <= 0) return null;

    return here.price - from.price;
  }

  /**
   * Decides what each option looks like, which is two different things.
   *
   * **Not offered on this ring** — no priced variant stands behind it — is *hidden*. A product's
   * price table is what says which combinations it sells: a ring that comes in three stones is
   * duplicated from a template carrying every stone, and the ones it doesn't sell have their rows
   * deleted. Those options would otherwise sit there greyed out forever, which reads as "sold out"
   * rather than "we don't make this".
   *
   * **Ruled out or sold out** stays visible and struck through, because there the buyer is meant to
   * see that the thing exists and this combination is the problem.
   */
  #renderOptionStates() {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    const priced = this.#pricedFromVariants();

    for (const group of config.groups) {
      let visible = 0;

      for (const option of group.options) {
        const control = this.#findControl(group.id, option.id);
        if (!control) continue;

        const hypothetical = { ...this.#selection, [group.id]: option.id };
        const variant = priced ? this.#matchVariant(hypothetical) : null;
        const unpriced = priced && (!variant || variant.price <= 0);
        const blocked =
          unpriced ||
          option.available === false ||
          config.rules.some((rule) => rule.unavailable && matchesRule(rule, hypothetical));

        control.disabled = blocked;

        const chip = control.closest('.product-customizer__option');
        chip?.toggleAttribute('data-unavailable', blocked && !unpriced);
        chip?.toggleAttribute('hidden', unpriced);
        if (!unpriced) visible += 1;

        this.#renderOptionPrice(group, option, control);
      }

      // A row where nothing is offered is not a row. Hiding the options but keeping the label would
      // leave the buyer looking at an empty heading.
      this.querySelector(`[data-group-id="${CSS.escape(group.id)}"]`)?.toggleAttribute('hidden', visible === 0);
    }
  }

  /**
   * Moves the selection off any option that turned out not to be offered here.
   *
   * The server renders the first option in each row as checked, and rules can rule out whatever is
   * checked once another row changes — either way the held selection can end up pointing at
   * something hidden, and every price and photo after that would be for a combination the buyer
   * cannot see.
   *
   * @returns {boolean} whether anything moved, so the caller can recalculate
   */
  #settleSelection() {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    if (!this.#pricedFromVariants()) return false;

    let moved = false;

    for (const group of config.groups) {
      const offered = group.options.filter((option) => {
        const variant = this.#matchVariant({ ...this.#selection, [group.id]: option.id });
        return variant && variant.price > 0;
      });

      if (offered.length === 0) continue;
      if (offered.some((option) => option.id === this.#selection[group.id])) continue;

      this.#selection[group.id] = offered[0].id;
      const control = this.#findControl(group.id, offered[0].id);
      if (control instanceof HTMLInputElement) control.checked = true;
      if (control instanceof HTMLOptionElement) control.selected = true;
      moved = true;
    }

    return moved;
  }

  /**
   * Writes the live delta onto an option chip. The markup only carries a price element when the
   * catalogue had a surcharge to render, so on a variant-priced product it has to be created.
   *
   * @param {CustomizerGroup} group
   * @param {CustomizerOption} option
   * @param {HTMLInputElement | HTMLOptionElement} control
   */
  #renderOptionPrice(group, option, control) {
    const delta = this.#liveDelta(group, option.id);
    if (delta === null) return;

    const label = delta === 0 ? '' : `${delta > 0 ? '+' : ''}${this.#money(delta)}`;
    const chip = control.closest('.product-customizer__option');
    if (!chip) return;

    // Swatch rows hide their text, so the chip's tooltip is where the number can still be read.
    chip.setAttribute('title', label ? `${option.label} (${label})` : option.label);

    const text = chip.querySelector('.product-customizer__option-text');
    if (!text) return;

    let price = text.querySelector('.product-customizer__option-price');
    if (!price) {
      if (!label) return;
      price = document.createElement('span');
      price.className = 'product-customizer__option-price';
      text.append(price);
    }
    price.innerHTML = label;
  }

  /**
   * Echoes the chosen option next to each group label, the way the theme's own variant picker does.
   *
   * @param {{chosen: {group: CustomizerGroup, option: CustomizerOption}[]}} result
   */
  #renderGroupLabels(result) {
    for (const { group, option } of result.chosen) {
      const label = this.querySelector(`[data-group-id="${CSS.escape(group.id)}"] [data-selected-label]`);
      if (!label) continue;

      label.textContent = option.label;

      // Icon and swatch groups have no room for a price under each chip, so the surcharge rides
      // along with the chosen option's name instead of being lost.
      const price = this.#liveDelta(group, option.id) ?? (Number(option.price) || 0);
      if (price !== 0) {
        const delta = document.createElement('span');
        delta.className = 'product-customizer__group-delta';
        delta.innerHTML = ` (${price > 0 ? '+' : ''}${this.#money(price)})`;
        label.append(delta);
      }
    }
  }

  /**
   * Picks the product media that pictures the current combination and brings it to the front of the
   * theme's own gallery, so zoom, thumbnails and the mobile carousel keep working.
   */
  #renderComboMedia() {
    const match = this.#resolveComboMedia();
    if (!match || match.id === this.#currentMediaId) return;

    const gallery = /** @type {any} */ ((this.#section ?? document).querySelector('media-gallery'));
    if (!gallery) return;

    const selector = `[data-media-id="${CSS.escape(String(match.id))}"]`;

    // Don't animate on first paint — only once the buyer starts configuring.
    const settled = this.#currentMediaId != null;
    this.#currentMediaId = match.id;

    // Only the slideshow is driven, so the combo photo swaps in place. A grid presentation shows
    // every photo at once, leaving nowhere to swap to — scrolling to the match there just dragged
    // the options panel off screen, so it is deliberately not handled.
    const slides = gallery.slideshow?.slides;
    if (slides) {
      const index = Array.from(slides).findIndex((slide) => /** @type {Element} */ (slide).querySelector(selector));
      if (index >= 0) gallery.slideshow.select(index, undefined, { animate: settled });
    }
  }

  /**
   * Resolves the current combination to one of the product's media items. Explicit `images` rules
   * win over the `image_pattern` convention; the most specific rule wins among them.
   *
   * @returns {{id: number, alt: string, src: string} | null}
   */
  #resolveComboMedia() {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    const media = config.media ?? [];
    if (media.length === 0) return null;

    let best = null;
    let bestScore = -1;

    for (const rule of config.comboImages ?? []) {
      if (!matchesRule(rule, this.#selection)) continue;

      const score = Object.keys(rule.when).length;
      if (score > bestScore) {
        bestScore = score;
        best = rule;
      }
    }

    if (best) {
      const found = this.#matchMedia([best.image ?? best.alt], media);
      if (found) return found;
    }

    if (config.imagePattern) {
      const found = this.#matchMedia(patternTokens(config, this.#selection), media);
      if (found) return found;
    }

    return null;
  }

  /**
   * The first media item whose **filename** carries every selected tag.
   *
   * The old rule was one exact string -- `round-sapphire-gold` had to be the whole alt text or the
   * whole filename. That made the photographer's naming part of the contract: a suffix, a different
   * separator or a word out of order and the photo stopped resolving, silently. Requiring the tags
   * instead lets `round-sapphire-gold-2.jpg` and `detail_gold_round_sapphire.jpg` both land.
   *
   * Alt text is no longer consulted. It is prose for screen readers, and keying behaviour to it
   * meant a typo there quietly changed which photograph a buyer saw.
   *
   * @param {(string | undefined)[]} tags
   * @param {{id: number, alt: string, src: string}[]} media
   * @returns {{id: number, alt: string, src: string} | null}
   */
  #matchMedia(tags, media) {
    const wanted = tags.map((tag) => String(tag ?? '').trim().toLowerCase()).filter(Boolean);
    if (wanted.length === 0) return null;

    return media.find((item) => {
      const filename = filenameOf(item.src);
      return wanted.every((tag) => hasTag(filename, tag));
    }) ?? null;
  }

  /** @param {{total: number, compareAt: number, message: string}} result */
  #renderPrice(result) {
    const { priceAmount, compareAmount, notice } = this.refs;

    if (priceAmount) priceAmount.innerHTML = this.#money(result.total);

    if (compareAmount) {
      const showCompare = result.compareAt > result.total;
      compareAmount.innerHTML = showCompare ? this.#money(result.compareAt) : '';
      compareAmount.hidden = !showCompare;
    }

    if (notice) {
      notice.textContent = result.message;
      notice.hidden = !result.message;
    }

    this.#syncThemePrice(result);
  }

  /**
   * Mirrors the configured total into the theme's own price block, so the page never shows two
   * different numbers.
   *
   * @param {{total: number, compareAt: number}} result
   */
  #syncThemePrice(result) {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    if (!config.syncThemePrice) return;

    const scope = this.#section ?? document;

    for (const container of scope.querySelectorAll('product-price [ref="priceContainer"]')) {
      const priceEl = container.querySelector('.price');
      if (priceEl) {
        // The visually hidden "Sale price"/"Regular price" label has to survive the rewrite.
        const label = priceEl.querySelector('.visually-hidden')?.outerHTML ?? '';
        priceEl.innerHTML = label + this.#money(result.total);
      }

      const compareEl = container.querySelector('.compare-at-price');
      if (compareEl && result.compareAt > result.total) {
        const label = compareEl.querySelector('.visually-hidden')?.outerHTML ?? '';
        compareEl.innerHTML = label + this.#money(result.compareAt);
      }
    }
  }

  /** @param {{chosen: {group: CustomizerGroup, option: CustomizerOption}[]}} result */
  #renderSummary(result) {
    const { summary } = this.refs;
    if (!summary) return;

    summary.innerHTML = '';

    for (const { group, option } of result.chosen) {
      const row = document.createElement('div');
      row.className = 'product-customizer__summary-row';

      const term = document.createElement('span');
      term.className = 'product-customizer__summary-label';
      term.textContent = group.label;

      const value = document.createElement('span');
      value.className = 'product-customizer__summary-value';
      value.textContent = option.label;

      row.append(term, value);
      summary.append(row);
    }
  }

  /**
   * Writes the selection into the product form as line item properties. Properties prefixed with an
   * underscore stay hidden from the buyer but still reach the order for fulfilment.
   *
   * @param {{total: number, chosen: {group: CustomizerGroup, option: CustomizerOption}[]}} result
   */
  #writeProperties(result) {
    for (const { group, option } of result.chosen) {
      const input = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`input[data-property-for="${CSS.escape(group.id)}"]`)
      );
      if (input) input.value = option.label;
    }

    const { configProperty, priceProperty } = this.refs;

    if (configProperty) {
      configProperty.value = result.chosen.map(({ group, option }) => `${group.id}:${option.id}`).join('|');
    }

    if (priceProperty) {
      priceProperty.value = String(result.total);
    }
  }

  /**
   * In `variant_match` mode the cart has to actually charge the configured price, so repoint the
   * form at the variant standing in for this combination. Without a match, adding to cart would
   * silently charge the wrong amount — block it instead of guessing.
   *
   * @param {{available: boolean, message: string}} result
   * @param {CustomizerVariant | null} match
   */
  #applyVariant(result, match) {
    const config = /** @type {CustomizerConfig} */ (this.#config);

    let sellable = result.available;
    let message = result.message;

    if (config.pricingMode === 'variant_match') {
      if (match) {
        const idInput = /** @type {HTMLInputElement | null} */ (
          document.querySelector(`form#${CSS.escape(config.formId)} input[name="id"]`)
        );
        if (idInput && idInput.value !== String(match.id)) idInput.value = String(match.id);

        // A row exists but carries no price: the combination is real and someone still has to say
        // what it costs. Selling it at zero would be worse than not selling it.
        if (match.price <= 0) {
          sellable = false;
          message ||= config.strings.noPriceVariant;
        }

        if (!match.available) {
          sellable = false;
          message ||= config.strings.comboSoldOut;
        }
      } else {
        sellable = false;
        message ||= config.strings.noPriceVariant;
      }
    }

    const { notice } = this.refs;
    if (notice && message !== result.message) {
      notice.textContent = message;
      notice.hidden = !message;
    }

    this.#toggleAddToCart(sellable);
  }

  /** @param {boolean} enabled */
  #toggleAddToCart(enabled) {
    const scope = this.#section ?? document;

    for (const container of scope.querySelectorAll('add-to-cart-component')) {
      // `enable`/`disable` are the theme's own API; the button fallback covers any other markup.
      if (enabled) {
        /** @type {any} */ (container).enable?.();
      } else {
        /** @type {any} */ (container).disable?.();
      }

      const button = container.querySelector('button');
      if (button) button.disabled = !enabled;
    }

    for (const accelerated of scope.querySelectorAll('accelerated-checkout-component')) {
      accelerated.toggleAttribute('hidden', !enabled);
    }
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const config = /** @type {CustomizerConfig} */ (this.#config);
    return formatMoney(cents, config.moneyFormat, config.currency);
  }
}

/**
 * Builds the combination key `image_pattern` describes — each `{group id}` replaced with the chosen
 * option's id, matched against the product's media in `#matchMedia`.
 *
 * A product may list photo stand-ins — `black_rhodium=silver` — for a choice it offers but has not
 * photographed yet. That option contributes the borrowed id instead of its own, so the key still
 * lands on media that exists and the gallery keeps swapping with the other rows. Left to itself the
 * lookup would miss and the gallery would sit on whichever photo happened to be up.
 *
 * The stand-ins belong to the product, not to the option: whether a finish has been photographed is
 * a fact about one ring's shoot, so the same finish on another ring is unaffected.
 *
 * @param {CustomizerConfig} config
 * @param {Record<string, string>} selection
 * @returns {string}
 */
function patternTokens(config, selection) {
  if (!config?.imagePattern) return [];

  /** @type {Record<string, string>} */
  const aliases = {};
  for (const line of config.photoAliases ?? []) {
    const [from, to] = String(line).split('=');
    if (from?.trim() && to?.trim()) aliases[from.trim()] = to.trim();
  }

  const tokens = [];
  for (const [, groupId] of String(config.imagePattern).matchAll(/{(\w+)}/g)) {
    const optionId = selection[groupId] ?? '';
    const value = aliases[optionId] || optionId;
    if (value) tokens.push(String(value));
  }

  return tokens;
}

/**
 * Does this filename carry the tag as a whole segment?
 *
 * A plain substring test would resolve `sapphire` inside `pink_sapphire`, so every sapphire photo
 * would also answer for pink sapphire. Tags are separated by `-`, `.` or spaces; `_` is part of a
 * tag, because option keys use it (`pink_sapphire`, `london_blue_topaz`).
 *
 * @param {string} filename
 * @param {string} tag
 * @returns {boolean}
 */
function hasTag(filename, tag) {
  const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9_])${escaped}([^a-z0-9_]|$)`).test(filename);
}

/**
 * Extracts the bare filename from a Shopify CDN url, dropping the transform query string.
 *
 * @param {string} src
 * @returns {string}
 */
function filenameOf(src) {
  return String(src ?? '')
    .split('?')[0]
    .split('/')
    .pop()
    .toLowerCase();
}

/**
 * Which catalogue rows the product prices through its own options, and the value each chip means.
 *
 * Linking a product option to `ring_opt_<row>` names every one of its values after the metaobject's
 * display field, so a row is wired exactly when its options' display names turn up among the
 * variants' option values. Reading it back this way means nothing has to be declared twice: the
 * product's own grid is the statement of which rows carry the price.
 *
 * @param {CustomizerConfig} config
 * @returns {Record<string, Record<string, string>> | null}
 */
function nativeRowsOf(config) {
  /** @type {Set<string>} */
  const values = new Set();
  for (const variant of config.variants) {
    for (const value of variant.options ?? []) values.add(String(value));
  }
  if (values.size === 0) return null;

  /** @type {Record<string, Record<string, string>>} */
  const rows = {};
  for (const group of config.groups) {
    /** @type {Record<string, string>} */
    const byOption = {};
    for (const option of group.options) {
      const display = String(option.display ?? '').trim();
      if (display && values.has(display)) byOption[option.id] = display;
    }
    if (Object.keys(byOption).length > 0) rows[group.id] = byOption;
  }

  return Object.keys(rows).length > 0 ? rows : null;
}

/**
 * Reads the configuration key off a variant SKU.
 *
 * Two shapes, both written by tools/ring_build.py:
 *   `CFG-sapphire-6k`  - the priced selection, when the product declares `price varies by`
 *   `CFG-181000`       - the shop-currency total, when it does not
 *
 * @param {CustomizerVariant} variant
 * @returns {string | null}
 */
function variantKey(variant) {
  const match = /^CFG-(.+)$/.exec(String(variant?.sku ?? '').trim());
  return match ? match[1] : null;
}

/**
 * Folds a rule's flat condition list into the `when` clause matching works on.
 *
 * Liquid cannot build a map, so a catalogue read straight from the metaobjects emits conditions as
 * `[{group, option}, ...]`. Two conditions naming the same row mean *either of these*, which is the
 * array form `when` already understands.
 *
 * @param {CustomizerRule & {conditions?: {group: string, option: string}[]}} rule
 * @returns {CustomizerRule}
 */
function withWhenClause(rule) {
  if (rule?.when || !Array.isArray(rule?.conditions)) return rule;

  /** @type {Record<string, string[]>} */
  const grouped = {};
  for (const condition of rule.conditions) {
    if (!condition?.group || !condition?.option) continue;
    (grouped[condition.group] ??= []).push(condition.option);
  }

  const when = Object.fromEntries(
    Object.entries(grouped).map(([group, options]) => [group, options.length === 1 ? options[0] : options])
  );
  return { ...rule, when };
}

/**
 * A rule applies when every key in its `when` clause matches the current selection. A value may be
 * an array, which matches if the selection is any member of it.
 *
 * @param {CustomizerRule} rule
 * @param {Record<string, string>} selection
 * @returns {boolean}
 */
function matchesRule(rule, selection) {
  const when = rule?.when;
  if (!when || typeof when !== 'object') return false;

  const entries = Object.entries(when);
  if (entries.length === 0) return false;

  return entries.every(([groupId, expected]) =>
    Array.isArray(expected) ? expected.includes(selection[groupId] ?? '') : selection[groupId] === expected
  );
}

if (!customElements.get('product-customizer')) {
  customElements.define('product-customizer', ProductCustomizer);
}
